/**
 * Bundle Selector Engine - Pat Nutrition
 * Supports two bundle types:
 * 
 * 1. STRUCTURED BUNDLE (priority)
 *    - custom.bundle_structure (JSON array)
 *    - Each item: { label, type, options }
 * 
 * 2. REPEAT BUNDLE (fallback)
 *    - custom.bundle_enabled + bundle_item_count + bundle_options
 *    - Same options repeated N times
 */

(function() {
  'use strict';

  document.addEventListener('DOMContentLoaded', initBundleSelector);

  function initBundleSelector() {
    const container = document.getElementById('bundle-selector');
    if (!container) return;

    // Detect bundle type
    const bundleType = container.dataset.bundleType || 'repeat';

    // Find the product form
    const form = container.closest('form');
    if (!form) {
      console.warn('[Bundle Selector] Could not find parent form');
      return;
    }

    // Find submit button
    const submitBtn = form.querySelector('button[type="submit"], input[type="submit"], [data-add-to-cart]');
    if (!submitBtn) {
      console.warn('[Bundle Selector] Could not find submit button');
      return;
    }

    // Route to appropriate initializer
    if (bundleType === 'structured') {
      initStructuredBundle(container, form, submitBtn);
    } else {
      initRepeatBundle(container, form, submitBtn);
    }
  }

  // ==========================================================================
  // STRUCTURED BUNDLE
  // ==========================================================================

  function initStructuredBundle(container, form, submitBtn) {
    // Parse structure JSON
    let structure;
    let rawData = container.dataset.structure;
    
    if (!rawData) {
      console.warn('[Bundle Selector] No structure data found');
      return;
    }

    try {
      // Decode HTML entities if present (from Liquid | escape filter)
      if (rawData.includes('&quot;') || rawData.includes('&#')) {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = rawData;
        rawData = textarea.value;
      }

      // Handle double-encoded JSON (string within string)
      let parsed = JSON.parse(rawData);
      
      // If we got a string, try parsing again (double-encoded)
      if (typeof parsed === 'string') {
        parsed = JSON.parse(parsed);
      }
      
      structure = parsed;
      
      if (!Array.isArray(structure) || structure.length === 0) {
        console.warn('[Bundle Selector] Invalid structure: not an array or empty');
        return;
      }
    } catch (e) {
      console.warn('[Bundle Selector] Failed to parse bundle_structure JSON:', e);
      return;
    }

    const totalFields = structure.length;
    const state = { selections: {} };

    // Build UI
    const root = document.createElement('div');
    root.className = 'bundle';

    // Header
    const header = createBundleHeader(totalFields);
    root.appendChild(header);

    // Status element reference
    const status = header.querySelector('.bundle__status');

    // List of fields
    const list = document.createElement('div');
    list.className = 'bundle__list';

    // Update function
    function update() {
      const chosen = Object.keys(state.selections).length;
      const statusText = status.querySelector('[data-bundle-status-text]');
      
      if (statusText) {
        statusText.textContent = `${chosen}/${totalFields} gekozen`;
      }

      const isComplete = chosen === totalFields;
      status.classList.toggle('is-complete', isComplete);
      updateStatusIcon(status, isComplete);

      // Enable/disable submit button
      if (isComplete) {
        submitBtn.disabled = false;
        submitBtn.classList.remove('is-bundle-disabled');
      } else {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-bundle-disabled');
      }

      // Update hidden inputs
      updateHiddenInputs(form, state.selections);
    }

    // Create fields based on structure
    structure.forEach(function(item, index) {
      const label = item.label || `Item ${index + 1}`;
      const type = item.type || 'select';
      const options = item.options || [];

      if (options.length === 0) {
        console.warn(`[Bundle Selector] No options for field: ${label}`);
        return;
      }

      const row = document.createElement('div');
      row.className = 'bundle__row';

      // Label
      const rowLabel = document.createElement('span');
      rowLabel.className = 'bundle__row-label';
      rowLabel.textContent = label;
      row.appendChild(rowLabel);

      // Currently only supporting "select" type
      if (type === 'select') {
        const select = document.createElement('select');
        select.className = 'bundle__select';
        select.required = true;
        select.dataset.key = label;

        // Placeholder
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = 'Please choose';
        placeholder.disabled = true;
        placeholder.selected = true;
        select.appendChild(placeholder);

        // Options
        options.forEach(function(opt) {
          const option = document.createElement('option');
          option.value = opt;
          option.textContent = opt;
          select.appendChild(option);
        });

        // Change handler
        select.addEventListener('change', function(e) {
          const value = e.target.value;
          
          if (value) {
            state.selections[label] = value;
            select.classList.add('is-selected', 'just-selected');
            setTimeout(function() {
              select.classList.remove('just-selected');
            }, 300);
          } else {
            delete state.selections[label];
            select.classList.remove('is-selected');
          }
          
          update();
        });

        row.appendChild(select);
      }
      // Future: add "buttons" type here

      list.appendChild(row);
    });

    root.appendChild(list);

    // Info message
    root.appendChild(createInfoMessage('Maak voor elk veld een keuze om door te gaan'));

    // Append to container
    container.appendChild(root);

    // Initialize random button
    initRandomButton(root, state, update);

    // Initial state
    submitBtn.disabled = true;
    submitBtn.classList.add('is-bundle-disabled');
    update();

    // Form submit validation
    form.addEventListener('submit', function(e) {
      const chosen = Object.keys(state.selections).length;
      if (chosen !== totalFields) {
        e.preventDefault();
        e.stopPropagation();
        alert('Maak alsjeblieft een keuze voor alle velden.');
        return false;
      }
    });

    // Listen for inject event
    form.addEventListener('bundle-inject', function() {
      updateHiddenInputs(form, state.selections);
    });
  }

  // ==========================================================================
  // REPEAT BUNDLE (existing logic)
  // ==========================================================================

  function initRepeatBundle(container, form, submitBtn) {
    const itemCount = parseInt(container.dataset.itemCount || '0', 10);
    const options = parseOptions(container.dataset.options);
    const label = container.dataset.optionLabel || 'Kies optie';
    const prefix = container.dataset.itemPrefix || 'Item';

    if (!itemCount || itemCount <= 0 || options.length === 0) {
      console.warn('[Bundle Selector] Missing required data:', { itemCount, options });
      return;
    }

    const state = { selections: {} };

    // Build UI
    const root = document.createElement('div');
    root.className = 'bundle';

    // Header
    const header = createBundleHeader(itemCount);
    root.appendChild(header);

    const status = header.querySelector('.bundle__status');

    // List
    const list = document.createElement('div');
    list.className = 'bundle__list';

    // Update function
    function update() {
      const chosen = Object.keys(state.selections).length;
      const statusText = status.querySelector('[data-bundle-status-text]');
      
      if (statusText) {
        statusText.textContent = `${chosen}/${itemCount} gekozen`;
      }

      const isComplete = chosen === itemCount;
      status.classList.toggle('is-complete', isComplete);
      updateStatusIcon(status, isComplete);

      if (isComplete) {
        submitBtn.disabled = false;
        submitBtn.classList.remove('is-bundle-disabled');
      } else {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-bundle-disabled');
      }

      updateHiddenInputs(form, state.selections);
    }

    // Create selects
    for (let i = 1; i <= itemCount; i++) {
      const row = document.createElement('div');
      row.className = 'bundle__row';

      const rowLabel = document.createElement('span');
      rowLabel.className = 'bundle__row-label';
      rowLabel.textContent = `${prefix} ${i}`;
      row.appendChild(rowLabel);

      const select = document.createElement('select');
      select.className = 'bundle__select';
      select.required = true;

      const key = `${prefix} ${i}`;
      select.dataset.key = key;

      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = `${label}...`;
      placeholder.disabled = true;
      placeholder.selected = true;
      select.appendChild(placeholder);

      options.forEach(function(opt) {
        const option = document.createElement('option');
        option.value = opt;
        option.textContent = opt;
        select.appendChild(option);
      });

      select.addEventListener('change', function(e) {
        const value = e.target.value;
        
        if (value) {
          state.selections[key] = value;
          select.classList.add('is-selected', 'just-selected');
          setTimeout(function() {
            select.classList.remove('just-selected');
          }, 300);
        } else {
          delete state.selections[key];
          select.classList.remove('is-selected');
        }
        
        update();
      });

      row.appendChild(select);
      list.appendChild(row);
    }

    root.appendChild(list);
    root.appendChild(createInfoMessage('Maak voor elk item een keuze om door te gaan'));

    container.appendChild(root);

    // Initialize random button
    initRandomButton(root, state, update);

    submitBtn.disabled = true;
    submitBtn.classList.add('is-bundle-disabled');
    update();

    form.addEventListener('submit', function(e) {
      const chosen = Object.keys(state.selections).length;
      if (chosen !== itemCount) {
        e.preventDefault();
        e.stopPropagation();
        alert('Maak alsjeblieft een keuze voor alle items in de bundel.');
        return false;
      }
    });

    form.addEventListener('bundle-inject', function() {
      updateHiddenInputs(form, state.selections);
    });
  }

  // ==========================================================================
  // SHARED HELPERS
  // ==========================================================================

  function createBundleHeader(totalItems) {
    const header = document.createElement('div');
    header.className = 'bundle__header';

    const titleRow = document.createElement('div');
    titleRow.className = 'bundle__title-row';

    const title = document.createElement('h3');
    title.className = 'bundle__title';
    title.innerHTML = `
      <span class="bundle__title-icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
          <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
          <line x1="12" y1="22.08" x2="12" y2="12"/>
        </svg>
      </span>
      Stel je bundel samen
    `;
    titleRow.appendChild(title);

    // Random button
    const randomBtn = document.createElement('button');
    randomBtn.type = 'button';
    randomBtn.className = 'bundle__random-btn';
    randomBtn.setAttribute('data-bundle-random', '');
    randomBtn.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="16 3 21 3 21 8"/>
        <line x1="4" y1="20" x2="21" y2="3"/>
        <polyline points="21 16 21 21 16 21"/>
        <line x1="15" y1="15" x2="21" y2="21"/>
        <line x1="4" y1="4" x2="9" y2="9"/>
      </svg>
      <span>Random</span>
    `;
    randomBtn.title = 'Vul willekeurig in';
    titleRow.appendChild(randomBtn);

    header.appendChild(titleRow);

    const status = document.createElement('div');
    status.className = 'bundle__status';
    status.innerHTML = `
      <span class="bundle__status-icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
      </span>
      <span data-bundle-status-text>0/${totalItems} gekozen</span>
    `;
    header.appendChild(status);

    return header;
  }

  // Random selection handler - fills all selects with random options
  function initRandomButton(container, state, updateCallback) {
    const randomBtn = container.querySelector('[data-bundle-random]');
    if (!randomBtn) return;

    randomBtn.addEventListener('click', function(e) {
      e.preventDefault();
      
      const selects = container.querySelectorAll('.bundle__select');
      
      selects.forEach(function(select) {
        // Get all non-placeholder options
        const options = Array.from(select.options).filter(opt => opt.value !== '');
        
        if (options.length > 0) {
          // Pick random option
          const randomIndex = Math.floor(Math.random() * options.length);
          const randomOption = options[randomIndex];
          
          // Set value
          select.value = randomOption.value;
          
          // Update state
          const key = select.dataset.key;
          if (key) {
            state.selections[key] = randomOption.value;
          }
          
          // Visual feedback
          select.classList.add('is-selected', 'just-selected');
          setTimeout(function() {
            select.classList.remove('just-selected');
          }, 300);
        }
      });

      // Trigger update
      updateCallback();
      
      // Button animation
      randomBtn.classList.add('is-clicked');
      setTimeout(function() {
        randomBtn.classList.remove('is-clicked');
      }, 300);
    });
  }

  function updateStatusIcon(status, isComplete) {
    const statusIcon = status.querySelector('.bundle__status-icon');
    if (!statusIcon) return;

    if (isComplete) {
      statusIcon.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
          <polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
      `;
    } else {
      statusIcon.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
      `;
    }
  }

  function createInfoMessage(text) {
    const info = document.createElement('div');
    info.className = 'bundle__info';
    info.innerHTML = `
      <svg class="bundle__info-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="16" x2="12" y2="12"/>
        <line x1="12" y1="8" x2="12.01" y2="8"/>
      </svg>
      <span>${text}</span>
    `;
    return info;
  }

  function updateHiddenInputs(form, selections) {
    // Remove existing bundle props
    form.querySelectorAll('[data-bundle-prop]').forEach(function(el) {
      el.remove();
    });

    // Add hidden inputs
    Object.entries(selections).forEach(function(entry) {
      const key = entry[0];
      const value = entry[1];
      
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + key + ']';
      input.value = value;
      input.setAttribute('data-bundle-prop', '1');
      form.appendChild(input);
    });
  }

  function parseOptions(data) {
    if (!data) return [];
    
    try {
      const parsed = JSON.parse(data);
      if (Array.isArray(parsed)) {
        return parsed.map(function(opt) {
          return String(opt).trim();
        }).filter(Boolean);
      }
      if (typeof parsed === 'string') {
        data = parsed;
      }
    } catch (e) {
      // Not valid JSON
    }
    
    // Try separators
    const separators = ['•', ',', '|', '\n'];
    for (let i = 0; i < separators.length; i++) {
      if (data.indexOf(separators[i]) !== -1) {
        return data.split(separators[i]).map(function(opt) {
          return opt.trim();
        }).filter(Boolean);
      }
    }
    
    const trimmed = data.trim();
    return trimmed ? [trimmed] : [];
  }

})();
